import { clsx } from 'clsx';
import { StatusBadge } from '../common/StatusBadge';
import { Button } from '../common/Button';
import type { Decision, PublicFramework } from '../../types';

interface ScorePreviewProps {
  scores: Record<string, { score: number | null; rationale: string }>;
  redFlags: string[];
  framework: PublicFramework;
  isSubmitting: boolean;
  onSubmit: () => void;
  onCancel?: () => void;
}

export function ScorePreview({
  scores,
  redFlags,
  framework,
  isSubmitting,
  onSubmit,
  onCancel,
}: ScorePreviewProps) {
  // Calculate total score using framework weights
  const { totalScore, isComplete, scoredCount, totalCriteria } = calculateScore(
    scores,
    framework
  );

  // Determine decision based on score and red flags
  const decision = determineDecision(totalScore, redFlags, framework.thresholds);

  const canSubmit = isComplete && !isSubmitting;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-wolf-900 border-t border-wolf-700/50 z-40">
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div>
            <span className="text-wolf-400 text-sm">Projected Score:</span>
            <span
              className={clsx(
                'ml-2 font-mono text-xl font-bold',
                totalScore !== null ? getScoreColor(totalScore) : 'text-wolf-500'
              )}
            >
              {totalScore !== null ? totalScore.toFixed(1) : '--'}
            </span>
            <span className="text-wolf-500 text-sm">/100</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-wolf-400 text-sm">Decision:</span>
            {decision ? (
              <StatusBadge status={decision} />
            ) : (
              <span className="text-wolf-500 text-sm">--</span>
            )}
          </div>
          <div className="text-wolf-500 text-xs">
            {scoredCount}/{totalCriteria} criteria
          </div>
        </div>

        <div className="flex items-center gap-3">
          {onCancel && (
            <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {isSubmitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function calculateScore(
  scores: Record<string, { score: number | null; rationale: string }>,
  framework: PublicFramework
): {
  totalScore: number | null;
  isComplete: boolean;
  scoredCount: number;
  totalCriteria: number;
} {
  let totalCriteria = 0;
  let scoredCount = 0;
  let isComplete = true;

  // Count criteria and check completeness
  for (const section of framework.sections) {
    for (const criterion of section.criteria) {
      totalCriteria++;
      const entry = scores[criterion.id];
      if (entry && entry.score !== null && entry.rationale.length >= 1 && entry.rationale.length <= 500) {
        scoredCount++;
      } else {
        isComplete = false;
      }
    }
  }

  // Calculate weighted score if all criteria scored
  if (scoredCount === 0) {
    return { totalScore: null, isComplete: false, scoredCount, totalCriteria };
  }

  // Mirrors the server's calculate_total_score (api/src/lib/claude.py):
  // each criterion score (1-5) normalized to 0-100, weighted by
  // criterion.weight within its section, then by section.weight.
  let totalScore = 0;
  for (const section of framework.sections) {
    let sectionScore = 0;
    for (const criterion of section.criteria) {
      const entry = scores[criterion.id];
      if (entry && entry.score !== null) {
        sectionScore += (((entry.score - 1) / 4) * 100) * criterion.weight;
      }
    }
    totalScore += sectionScore * section.weight;
  }
  // Server rounds to 2dp before applying decision thresholds (round(total_score, 2))
  totalScore = Math.round(totalScore * 100) / 100;

  return { totalScore, isComplete, scoredCount, totalCriteria };
}

function determineDecision(
  score: number | null,
  redFlags: string[],
  thresholds: { GREEN: number; CONDITIONAL: number; HOLD: number }
): Decision | null {
  if (score === null) return null;
  if (redFlags.length > 0) return 'BLOCKED';
  if (score >= thresholds.GREEN) return 'GREEN';
  if (score >= thresholds.CONDITIONAL) return 'CONDITIONAL';
  if (score >= thresholds.HOLD) return 'HOLD';
  return 'PASS';
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-action';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}
